// Formatting + yield-variance severity helpers for the production + pack-conversion
// screens (plan.md G21). Money/mass math of record always comes from `@zebra/core`
// (yieldCalc.ts, moisture.ts) — everything here is presentation-only, mirroring the
// identical local-helper convention every other web feature uses (see e.g.
// features/purchasing-intake/common.ts, features/stores-transfers/format.ts) rather
// than importing a sibling feature's helpers (S0: each web goal owns its own files).
import type { BaseUom, RunMode } from '@zebra/core';

/** Plain-English label for `RunMode` (guidance wave, 2026-08-25) — the code's
 * `SHIFT`/`BATCH` pair reads as jargon; the rest of the app (route names,
 * page copy) already calls these "continuous" runs and "kiln batches". Feature-
 * local (not `lib/enumLabels.ts`) since this union is production-packconv's own
 * (mirrors `CustomersAndPrices.tsx`'s local `INVOICING_MODE_LABELS` precedent). */
export const RUN_MODE_LABELS: Record<RunMode, string> = {
  SHIFT: 'Continuous',
  BATCH: 'Batch',
};

export const RUN_MODE_DESCRIPTIONS: Record<RunMode, string> = {
  SHIFT: 'Tracks a shift’s steady inputs, consumables and outputs as they happen.',
  BATCH: 'Tracks a single kiln load from cages-in to cages-out, opened and closed as one unit.',
};

/** ZAR cents (bigint, or its wire numeric-string/number form) -> "R1 234,56"
 * (en-ZA: space-thousands, comma-decimal — SA convention, not US). Grouping
 * goes through `Intl.NumberFormat('en-ZA').format(rand)` on the bigint
 * whole-rand value directly (its `.format` overload accepts `bigint`, no
 * `Number(cents)` conversion), so precision is exact even for amounts beyond
 * Number.MAX_SAFE_INTEGER; the cents remainder is likewise derived from the
 * bigint's own `% 100n`, never floating point. */
export function formatMoneyCents(cents: bigint | string | number): string {
  const value = typeof cents === 'bigint' ? cents : BigInt(Math.trunc(Number(cents)));
  const negative = value < 0n;
  const abs = negative ? -value : value;
  const rand = abs / 100n;
  const remainder = (abs % 100n).toString().padStart(2, '0');
  const grouped = new Intl.NumberFormat('en-ZA').format(rand);
  return `${negative ? '-' : ''}R${grouped},${remainder}`;
}

export function formatQty(value: string | number | null | undefined, decimals = 3): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString('en-ZA', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

export function formatPct(value: string | number | null | undefined, decimals = 1): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(decimals)}%`;
}

/** Unit-aware label for a quantity input that captures one product line
 * (BUILD_SPEC.md §5.2 base_uom KG|EACH|L). Mirrors the server's baseUom-scoped
 * treatment of quantity (services/api/src/modules/production/runs.ts's `/post`
 * handler joins inputs/outputs with `products.base_uom` and scopes the dry-mass
 * basis to KG lines only, R2/R6): an EACH product's qty column holds a unit
 * count, not a mass, so the input asking for it must say so. `undefined`
 * (nothing selected yet) and `'L'` (out of this goal's scope) keep whatever
 * kg-labelled text the caller already used — only EACH changes wording. */
export function qtyInputLabel(baseUom: BaseUom | undefined, kgLabel: string, eachLabel: string): string {
  return baseUom === 'EACH' ? eachLabel : kgLabel;
}

/** Unit-aware per-row quantity display for tables that can mix KG- and
 * EACH-denominated lines within the same document (e.g. a SHIFT run's inputs/
 * outputs are not all one product) — same baseUom scoping as `qtyInputLabel`,
 * applied per row instead of to a single table-wide header. EACH renders as a
 * whole count with no decimals ("counted not weighed", per base_uom semantics);
 * KG keeps the existing 3dp kg formatting. */
export function formatLineQty(qty: string | number | null | undefined, baseUom: BaseUom | undefined): string {
  if (baseUom === 'EACH') {
    if (qty === null || qty === undefined || qty === '') return '—';
    const n = Number(qty);
    return Number.isFinite(n) ? `${n.toLocaleString('en-ZA', { maximumFractionDigits: 0 })} units` : '—';
  }
  return `${formatQty(qty)} kg`;
}

export function formatSignedPct(value: string | number | null | undefined, decimals = 1): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${n > 0 ? '+' : ''}${n.toFixed(decimals)}%`;
}

export function formatDateTime(value: Date | string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** `datetime-local` input default: "now", truncated to the minute (mirrors
 * features/purchasing-intake/common.ts's identical helper — native inputs are
 * faster to drive on a touchscreen than a custom calendar popover, §11). */
export function nowForDatetimeLocal(): string {
  const d = new Date();
  d.setSeconds(0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** `date` input default: "today" (`YYYY-MM-DD`), for date-only fields like a
 * recipe's `effectiveFrom`. */
export function todayForDateInput(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export type VarianceSeverity = 'ok' | 'warn' | 'high';

const WARN_PP = 3; // percentage points
const HIGH_PP = 8;

/** Classifies a yield-variance percentage-point delta for badge coloring. Exact
 * bands are a UI-only judgment call (BUILD_SPEC.md §8.3 says yield is "shown vs
 * recipe" but pins no thresholds) — mirrors the identical judgment call already
 * made for silo/count variance bands (features/silo-counts/utils.ts). */
export function yieldVarianceSeverity(variancePct: number | string | null): VarianceSeverity {
  if (variancePct === null) return 'ok';
  const n = Number(variancePct);
  if (!Number.isFinite(n)) return 'ok';
  const abs = Math.abs(n);
  if (abs >= HIGH_PP) return 'high';
  if (abs >= WARN_PP) return 'warn';
  return 'ok';
}

export function severityColor(severity: VarianceSeverity): string {
  switch (severity) {
    case 'high':
      return 'red';
    case 'warn':
      return 'amber';
    default:
      return 'green';
  }
}
